<template>
  <section class="world-intel">
    <header class="widget-header">
      <h2>World Intel</h2>
      <p class="kicker">
        Scout reports, counter-intelligence, and city defenses
        <router-link to="/game/world">View map</router-link>
      </p>
    </header>

    <div class="bucket-list">
      <section
        v-for="bucket in buckets"
        :key="bucket.id"
        class="intel-bucket"
      >
        <div class="bucket-head">
          <h3>
            {{ bucket.label }}
            <span class="count">{{ bucket.items.length }}</span>
          </h3>
        </div>
        <p v-if="bucket.items.length === 0" class="empty-copy">
          {{ bucket.empty }}
        </p>
        <ul v-else class="intel-rows">
          <li v-for="item in bucket.items" :key="item.key">
            <button type="button" class="intel-row" @click="openRow(item.report)">
              <span class="intel-title">{{ item.title }}</span>
              <span class="intel-meta" :class="item.tone">{{ item.meta }}</span>
            </button>
          </li>
        </ul>
      </section>
    </div>

    <IntelReportModal :report="openReport" @close="openReport = null" />
    <BattleDetailedReportModal
      :is-visible="!!openBattle"
      :battle-result="openBattle?.result || null"
      :attacker-race="openBattle?.result?.attackerRace || ''"
      :defender-race="openBattle?.result?.defenderRace || ''"
      :perspective="openBattle?.perspective || 'defender'"
      @close="openBattle = null"
    />
  </section>
</template>

<script setup>
import { computed, onMounted, ref } from "vue";
import { useGameStore } from "../store/game";
import IntelReportModal from "./IntelReportModal.vue";
import BattleDetailedReportModal from "./BattleDetailedReportModal.vue";
import {
  battleReportIdFromIntel,
  intelReportMeta,
  intelReportTitle,
  intelReportTone,
  isDefenseReport,
} from "../utils/intelReports";

const gameStore = useGameStore();
const openReport = ref(null);
const openBattle = ref(null);

const buckets = computed(() => {
  const reports = Array.isArray(gameStore.intelReports)
    ? gameStore.intelReports
    : [];
  const unread = reports.filter((report) => !report.read_at);
  const recent = reports.filter((report) => report.read_at).slice(0, 8);

  return [
    {
      id: "new",
      label: "New",
      empty: "No new intel.",
      items: unread.map((report) => ({
        key: `new-${report.id}`,
        title: intelReportTitle(report),
        meta: intelReportMeta(report),
        tone: intelReportTone(report) || "new",
        report,
      })),
    },
    {
      id: "recent",
      label: "Recent",
      empty: "No reports yet.",
      items: recent.map((report) => ({
        key: `recent-${report.id}`,
        title: intelReportTitle(report),
        meta: intelReportMeta(report),
        tone: intelReportTone(report),
        report,
      })),
    },
  ];
});

async function openRow(report) {
  if (report?.id && !report.read_at) {
    try {
      const updated = await gameStore.markIntelRead(report.id);
      if (updated) report = updated;
    } catch {
      /* keep local */
    }
  }
  if (isDefenseReport(report)) {
    openReport.value = null;
    const battleId = battleReportIdFromIntel(report);
    if (!battleId) return;
    try {
      openBattle.value = await gameStore.fetchBattleReport(battleId);
    } catch {
      openBattle.value = null;
    }
    return;
  }
  openBattle.value = null;
  openReport.value = report;
}

onMounted(async () => {
  try {
    await gameStore.fetchWorldIntel();
  } catch {
    /* empty buckets */
  }
});
</script>

<style scoped>
.world-intel {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  padding: 0.9rem 1rem 1rem;
  box-sizing: border-box;
  color: #e7ecf1;
}

.widget-header h2 {
  margin: 0;
  font-family: "Cinzel", serif;
  font-size: 0.78rem;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #c9b48a;
}

.kicker {
  margin: 0.15rem 0 0;
  font-size: 0.72rem;
  color: #88c0d0;
}

.kicker a {
  margin-left: 0.35rem;
  color: #00f0ff;
  text-decoration: none;
}

.kicker a:hover {
  text-decoration: underline;
}

.bucket-list {
  margin-top: 0.7rem;
  overflow: auto;
  min-height: 0;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.7rem;
}

.bucket-head {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 0.5rem;
}

.bucket-head h3 {
  margin: 0 0 0.35rem;
  min-width: 0;
  font-family: "Cinzel", serif;
  font-size: 0.7rem;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: #c9b48a;
}

.count {
  margin-left: 0.35rem;
  color: #00f0ff;
}

.empty-copy {
  margin: 0;
  color: #8892b0;
  font-size: 0.78rem;
  font-style: italic;
}

.intel-rows {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
}

.intel-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
  padding: 0.35rem 0.5rem;
  background: rgba(0, 240, 255, 0.08);
  border: 1px solid rgba(0, 240, 255, 0.22);
  border-radius: 4px;
  color: #d8dee9;
  font-size: 0.8rem;
  cursor: pointer;
  text-align: left;
}

.intel-row:hover {
  background: rgba(0, 240, 255, 0.16);
  border-color: #00f0ff;
}

.intel-title {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.intel-meta {
  flex-shrink: 0;
  font-size: 0.68rem;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #88c0d0;
}

.intel-meta.new {
  color: #ebcb8b;
  font-weight: 700;
}

.intel-meta.held {
  color: #8fbc8f;
  font-weight: 700;
}

.intel-meta.fallen {
  color: #bf616a;
  font-weight: 700;
}
</style>
